import { AccountReasonNoTinOptions } from '@/utils/accountTypes';
import { AccountTypeFlags, OpenAccountMetadataOption, OpenAccountStage } from './types';

export interface FeeType {
  platformProcessingFee: string[];
  annualAccountFeeAdministration: string[];
  annualAccountFeeRenumeration: string[];
  annualServiceFee: string[];
  initialFee: string[];
  etfEntryFee: string[];
  equitiesEntryFee: string[];
  structuredNotesEntryFee: string;
  minimumCashBalance: string[];
  feeSchedulesId: any;
}

export function findMetadataOption<T extends string | number>(
  value: T,
  options: readonly OpenAccountMetadataOption<T>[],
): OpenAccountMetadataOption<T> | null {
  const match: OpenAccountMetadataOption<T> | undefined = options?.find(
    (option) => option.value === value,
  );
  return match ? match : null;
}

export function findReasonOption(v?: string | null) {
  return AccountReasonNoTinOptions.find((o) => o.value === v) || null;
}

export function getFeeSchedule(
  instantOrTerm: number,
  classicOrFlexible: number,
): number {
  if (instantOrTerm === 1 && classicOrFlexible === 1) {
    return 1;
  } else if (instantOrTerm === 1 && classicOrFlexible === 3) {
    return 5;
  } else if (instantOrTerm === 2 && classicOrFlexible === 1) {
    return 3;
  } else if (instantOrTerm === 2 && classicOrFlexible === 3) {
    return 6;
  } else if (instantOrTerm === 1 && classicOrFlexible === 7) {
    return 7;
  }
  return 1;
}

function getFeeSchedulesId(instantOrTerm: number, classicOrFlexible: number) {
  const feeSchedulesId = {
    InstantAccessClassic: false,
    TermClassicFee: false,
    InstantClassicFlex: false,
    TermClassicFlexFee: false,
    /** Instant Access + AMA ClassicFlex (FeeSchedulesId 7) — not the same as Term ClassicFlex */
    AmaClassicFlexInstant: false,
  };

  if (instantOrTerm === 1 && classicOrFlexible === 1) {
    return { ...feeSchedulesId, InstantAccessClassic: true };
  } else if (instantOrTerm === 1 && classicOrFlexible === 3) {
    return { ...feeSchedulesId, InstantClassicFlex: true };
  } else if (instantOrTerm === 2 && classicOrFlexible === 1) {
    return { ...feeSchedulesId, TermClassicFee: true };
  } else if (instantOrTerm === 2 && classicOrFlexible === 3) {
    return { ...feeSchedulesId, TermClassicFlexFee: true };
  } else if (instantOrTerm === 1 && classicOrFlexible === 7) {
    return { ...feeSchedulesId, AmaClassicFlexInstant: true };
  }
  return feeSchedulesId;
}

export function findInstantAndClassic(
  FeeSchedulesId: number,
): { instantOrTerm: number; classicOrFlexible: number } {
  switch (FeeSchedulesId) {
    case 1:
      return { instantOrTerm: 1, classicOrFlexible: 1 };
    case 2:
      return { instantOrTerm: 1, classicOrFlexible: 2 };
    case 3:
      return { instantOrTerm: 2, classicOrFlexible: 1 };
    case 4:
      return { instantOrTerm: 2, classicOrFlexible: 2 };
    case 5:
      return { instantOrTerm: 1, classicOrFlexible: 3 };
    case 6:
      return { instantOrTerm: 2, classicOrFlexible: 3 };
    case 7:
      return { instantOrTerm: 1, classicOrFlexible: 7 };
    default:
      return { instantOrTerm: 1, classicOrFlexible: 1 };
  }
}

export function getAccountType(accountType: number): AccountTypeFlags {
  const accountTypes = {
    standAlone: false,
    qrops: false,
    coroprate: false,
    trust: false,
    masterAccount: false,
  };

  switch (accountType) {
    case 400159:
      return { ...accountTypes, standAlone: true };
    case 400160:
      return { ...accountTypes, qrops: true };
    case 400163:
      return { ...accountTypes, coroprate: true };
    case 400164:
      return { ...accountTypes, trust: true };
    case 400166:
      return { ...accountTypes, masterAccount: true };
    default:
      return accountTypes;
  }
}

export function getStageAccountType(accountType: {
  standAlone: boolean;
  qrops: boolean;
  coroprate: boolean;
  trust: boolean;
  masterAccount: boolean;
}) {
  const stages: OpenAccountStage[] = [
    {
      title: 'Account Type Selection',
      steps: [{ title: 'Account type' }, { title: 'Confirm details' }],
      valid: true,
    },
    {
      title: 'Personal Details',
      steps: [
        { title: 'First account holder details' },
        { title: 'Correspondence information' },
        { title: 'Disbursement account details' },
      ],
      valid: false,
    },
    {
      title: 'Investment Profile',
      steps: [{ title: 'Risk profile' }, { title: 'Appropriateness test' }],
      valid: false,
    },
    { title: 'Fees and Charges', steps: [], valid: false },
    {
      title: 'Acknowledgements',
      steps: [
        { title: 'Discretionary management services' },
        { title: 'Business conditions' },
      ],
      valid: false,
    },
    { title: 'Sign', steps: [], valid: false },
    { title: 'Finish', steps: [], valid: false },
  ];

  if (accountType.qrops || accountType.trust) {
    stages.splice(2, 1);
    stages[1].steps.splice(2, 2);
  }

  return stages;
}

export function getProcessingFee(
  instantOrTerm: number,
  classicOrFlexible: number,
): FeeType {
  const feeSchedulesId = getFeeSchedulesId(instantOrTerm, classicOrFlexible);

  if (feeSchedulesId.InstantAccessClassic) {
    return {
      platformProcessingFee: [
        '0%',
        '0.5%',
        '1%',
        '1.5%',
        '2%',
        '2.5%',
        '3%',
        '3.5%',
        '4%',
        '4.5%',
        '5%',
      ],
      annualAccountFeeAdministration: ['EUR 212', 'EUR 112'],
      annualAccountFeeRenumeration: ['EUR 164', 'EUR 64'],
      annualServiceFee: [
        '0.20%',
        '0.25%',
        '0.30%',
        '0.35%',
        '0.40%',
        '0.45%',
        '0.50%',
        '0.55%',
        '0.60%',
        '0.65%',
        '0.70%',
        '0.75%',
        '0.80%',
        '0.85%',
        '0.90%',
        '0.95%',
        '1.00%',
        '1.05%',
        '1.10%',
        '1.15%',
        '1.20%',
        '1.25%',
        '1.30%',
        '1.35%',
        '1.40%',
        '1.45%',
        '1.50%',
        '1.55%',
        '1.60%',
        '1.65%',
        '1.70%',
        '1.75%',
        '1.80%',
        '1.85%',
        '1.90%',
        '1.95%',
        '2.00%',
        '2.05%',
        '2.10%',
        '2.15%',
        '2.20%',
        '2.25%',
        '2.30%',
        '2.35%',
        '2.40%',
        '2.45%',
        '2.50%'
      ],
      initialFee: [
        '0%',
        '0.5%',
        '1%',
        '1.5%',
        '2%',
        '2.5%',
        '3%',
        '3.5%',
        '4%',
        '4.5%',
        '5%',
        '100%',
      ],
      etfEntryFee: ['0%', '1%', '2%', '3%', '4%', '5%'],
      equitiesEntryFee: ['0%', '1%', '2%', '3%', '4%', '5%'],
      structuredNotesEntryFee: '1.6%',
      minimumCashBalance: ['1%', '2%', '3%', '4%', '5%'],
      feeSchedulesId,
    };
  } else if (feeSchedulesId.TermClassicFee) {
    return {
      platformProcessingFee: [
        '1%',
        '2%',
        '3%',
        '4%',
        '5%',
        '6%',
        '7%',
        '8%',
        '9%',
        '10%',
      ],
      annualAccountFeeAdministration: ['EUR 212', 'EUR 112'],
      annualAccountFeeRenumeration: ['EUR 164', 'EUR 64'],
      annualServiceFee: [
        '0.20%',
        '0.25%',
        '0.30%',
        '0.35%',
        '0.40%',
        '0.45%',
        '0.50%',
        '0.55%',
        '0.60%',
        '0.65%',
        '0.70%',
        '0.75%',
        '0.80%',
        '0.85%',
        '0.90%',
        '0.95%',
        '1.00%',
        '1.05%',
        '1.10%',
        '1.15%',
        '1.20%',
        '1.25%',
        '1.30%',
        '1.35%',
        '1.40%',
        '1.45%',
        '1.50%',
        '1.55%',
        '1.60%',
        '1.65%',
        '1.70%',
        '1.75%',
        '1.80%',
        '1.85%',
        '1.90%',
        '1.95%',
        '2.00%',
        '2.05%',
        '2.10%',
        '2.15%',
        '2.20%',
        '2.25%',
        '2.30%',
        '2.35%',
        '2.40%',
        '2.45%',
        '2.50%'
      ],
      initialFee: ['0%', '0.5%', '1%', '1.5%', '2%', '2.5%', '3%', '3.5%', '4%', '4.5%', '5%', '100%'],
      etfEntryFee: ['0%', '1%', '2%', '3%', '4%', '5%'],
      equitiesEntryFee: ['0%', '1%', '2%', '3%', '4%', '5%'],
      structuredNotesEntryFee: '1.6%',
      minimumCashBalance: ['1%', '2%', '3%', '4%', '5%'],
      feeSchedulesId,
    };
  } else if (feeSchedulesId.InstantClassicFlex) {
    return {
      platformProcessingFee: [
        '0%',
        '0.5%',
        '1%',
        '1.5%',
        '2%',
        '2.5%',
        '3%',
        '3.5%',
        '4%',
        '4.5%',
        '5%',
      ],
      annualAccountFeeAdministration: ['EUR 150'],
      annualAccountFeeRenumeration: ['EUR 64'],
      annualServiceFee: [
        '0.20%',
        '0.25%',
        '0.30%',
        '0.35%',
        '0.40%',
        '0.45%',
        '0.50%',
        '0.55%',
        '0.60%',
        '0.65%',
        '0.70%',
        '0.75%',
        '0.80%',
        '0.85%',
        '0.90%',
        '0.95%',
        '1.00%',
        '1.05%',
        '1.10%',
        '1.15%',
        '1.20%',
        '1.25%',
        '1.30%',
        '1.35%',
        '1.40%',
        '1.45%',
        '1.50%',
        '1.55%',
        '1.60%',
        '1.65%',
        '1.70%',
        '1.75%',
        '1.80%',
        '1.85%',
        '1.90%',
        '1.95%',
        '2.00%',
        '2.05%',
        '2.10%',
        '2.15%',
        '2.20%',
        '2.25%',
        '2.30%',
        '2.35%',
        '2.40%',
        '2.45%',
        '2.50%'
      ],
      initialFee: [
        '0%',
        '0.5%',
        '1%',
        '1.5%',
        '2%',
        '2.5%',
        '3%',
        '3.5%',
        '4%',
        '4.5%',
        '5%',
        '100%',
      ],
      etfEntryFee: ['0%', '1%', '2%', '3%', '4%', '5%'],
      equitiesEntryFee: ['0%', '1%', '2%', '3%', '4%', '5%'],
      structuredNotesEntryFee: '1.6%',
      minimumCashBalance: ['1%', '2%', '3%', '4%', '5%'],
      feeSchedulesId,
    };
  } else if (feeSchedulesId.AmaClassicFlexInstant) {
    // AMA / AIN Instant Access + AMA ClassicFlex: same processing grid as Instant ClassicFlex (0%–5%, step 0.5%)
    return {
      platformProcessingFee: [
        '0%',
        '0.5%',
        '1%',
        '1.5%',
        '2%',
        '2.5%',
        '3%',
        '3.5%',
        '4%',
        '4.5%',
        '5%',
      ],
      annualAccountFeeAdministration: ['EUR 150'],
      annualAccountFeeRenumeration: ['EUR 64'],
      annualServiceFee: [
        '0.20%',
        '0.25%',
        '0.30%',
        '0.35%',
        '0.40%',
        '0.45%',
        '0.50%',
        '0.55%',
        '0.60%',
        '0.65%',
        '0.70%',
        '0.75%',
        '0.80%',
        '0.85%',
        '0.90%',
        '0.95%',
        '1.00%',
        '1.05%',
        '1.10%',
        '1.15%',
        '1.20%',
        '1.25%',
        '1.30%',
        '1.35%',
        '1.40%',
        '1.45%',
        '1.50%',
        '1.55%',
        '1.60%',
        '1.65%',
        '1.70%',
        '1.75%',
        '1.80%',
        '1.85%',
        '1.90%',
        '1.95%',
        '2.00%',
        '2.05%',
        '2.10%',
        '2.15%',
        '2.20%',
        '2.25%',
        '2.30%',
        '2.35%',
        '2.40%',
        '2.45%',
        '2.50%'
      ],
      initialFee: [
        '0%',
        '0.5%',
        '1%',
        '1.5%',
        '2%',
        '2.5%',
        '3%',
        '3.5%',
        '4%',
        '4.5%',
        '5%',
        '100%',
      ],
      etfEntryFee: ['0%', '1%', '2%', '3%', '4%', '5%'],
      equitiesEntryFee: ['0%', '1%', '2%', '3%', '4%', '5%'],
      structuredNotesEntryFee: '1.6%',
      minimumCashBalance: ['1%', '2%', '3%', '4%', '5%'],
      feeSchedulesId,
    };
  } else if (feeSchedulesId.TermClassicFlexFee) {
    return {
      platformProcessingFee: [
        '1%',
        '2%',
        '3%',
        '4%',
        '5%',
        '6%',
        '7%',
        '8%',
        '9%',
        '10%',
      ],
      annualAccountFeeAdministration: ['EUR 150'],
      annualAccountFeeRenumeration: ['EUR 64'],
      annualServiceFee: [
        '0.20%',
        '0.25%',
        '0.30%',
        '0.35%',
        '0.40%',
        '0.45%',
        '0.50%',
        '0.55%',
        '0.60%',
        '0.65%',
        '0.70%',
        '0.75%',
        '0.80%',
        '0.85%',
        '0.90%',
        '0.95%',
        '1.00%',
        '1.05%',
        '1.10%',
        '1.15%',
        '1.20%',
        '1.25%',
        '1.30%',
        '1.35%',
        '1.40%',
        '1.45%',
        '1.50%',
        '1.55%',
        '1.60%',
        '1.65%',
        '1.70%',
        '1.75%',
        '1.80%',
        '1.85%',
        '1.90%',
        '1.95%',
        '2.00%',
        '2.05%',
        '2.10%',
        '2.15%',
        '2.20%',
        '2.25%',
        '2.30%',
        '2.35%',
        '2.40%',
        '2.45%',
        '2.50%'
      ],
      initialFee: [
        '0%',
        '0.5%',
        '1%',
        '1.5%',
        '2%',
        '2.5%',
        '3%',
        '3.5%',
        '4%',
        '4.5%',
        '5%',
        '100%',
      ],
      etfEntryFee: ['0%', '1%', '2%', '3%', '4%', '5%'],
      equitiesEntryFee: ['0%', '1%', '2%', '3%', '4%', '5%'],
      structuredNotesEntryFee: '1.6%',
      minimumCashBalance: ['1%', '2%', '3%', '4%', '5%'],
      feeSchedulesId,
    };
  }

  return {
    platformProcessingFee: [
      '0%',
      '0.5%',
      '1%',
      '1.5%',
      '2%',
      '2.5%',
      '3%',
      '3.5%',
      '4%',
      '4.5%',
      '5%',
    ],
    annualAccountFeeAdministration: ['EUR 212', 'EUR 112'],
    annualAccountFeeRenumeration: ['EUR 164', 'EUR 64'],
    annualServiceFee: [
      '0.20%',
      '0.25%',
      '0.30%',
      '0.35%',
      '0.40%',
      '0.45%',
      '0.50%',
      '0.55%',
      '0.60%',
      '0.65%',
      '0.70%',
      '0.75%',
      '0.80%',
      '0.85%',
      '0.90%',
      '0.95%',
      '1.00%',
      '1.05%',
      '1.10%',
      '1.15%',
      '1.20%',
      '1.25%',
      '1.30%',
      '1.35%',
      '1.40%',
      '1.45%',
      '1.50%',
      '1.55%',
      '1.60%',
      '1.65%',
      '1.70%',
      '1.75%',
      '1.80%',
      '1.85%',
      '1.90%',
      '1.95%',
      '2.00%',
      '2.05%',
      '2.10%',
      '2.15%',
      '2.20%',
      '2.25%',
      '2.30%',
      '2.35%',
      '2.40%',
      '2.45%',
      '2.50%'
    ],
    initialFee: [
      '0%',
      '0.5%',
      '1%',
      '1.5%',
      '2%',
      '2.5%',
      '3%',
      '3.5%',
      '4%',
      '4.5%',
      '5%',
      '100%',
    ],
    etfEntryFee: ['1%', '2%', '3%', '4%', '5%'],
    equitiesEntryFee: ['1%', '2%', '3%', '4%', '5%'],
    structuredNotesEntryFee: '1.6%',
    minimumCashBalance: ['1%', '2%', '3%', '4%', '5%'],
    feeSchedulesId,
  };
}

export function dateIsValid(year: number, month: number, date: number): boolean {
  const monthIndex = month - 1;
  const dateObj: Date = new Date(year, monthIndex, date);
  return dateObj.getMonth() === monthIndex && dateObj.getDate() === date;
}
